import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Check, Plus, X } from '../../../../../components/icon/solar-icons.js'
import { PortalAbsenceBadge } from '../../../../../components/portal-absence-badge/portal-absence-badge.js'
import { PortalConfirmDialog } from '../../../../../components/portal-confirm-dialog/portal-confirm-dialog.js'
import type { LeavePolicyBundle, SettingsPolicy } from '../../../utils/portal-api.js'
import { PickerEmployee, PolicyEmployeePicker } from './policy-employee-picker.js'

export interface LeavePolicyBundlesSectionProps {
    lang: Language
    bundles: LeavePolicyBundle[]
    // All type-configs (active) selectable for a bundle.
    policies: SettingsPolicy[]
    slideOpen: boolean
    editingBundle: LeavePolicyBundle | null
    bundleForm: { name: string; policyIds: number[] }
    saving: boolean
    error: string | null
    onOpenCreate: () => void
    onOpenEdit: (bundle: LeavePolicyBundle) => void
    onClose: () => void
    onUpdateName: (value: string) => void
    onTogglePolicy: (policyId: number) => void
    onSave: () => void
    onDelete: (id: number) => void
    // Member management (PolicyEmployeePicker) — every member is added and
    // removed by hand; there is no auto-assignment.
    employeeIds: number[]
    selectedEmployees: PickerEmployee[]
    onAddEmployee: (emp: PickerEmployee) => void
    onRemoveEmployee: (id: number) => void
    onSelectAllEmployees: (employees: PickerEmployee[]) => void
    onClearAllEmployees: () => void
}

export const LeavePolicyBundlesSection = (props: LeavePolicyBundlesSectionProps) => {
    const { lang, bundles, policies, slideOpen, editingBundle, bundleForm, saving, error } = props
    const s = t.portal.settings.policies
    const b = t.portal.settings.policies.bundles
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)

    const accrualLabel = (m: string) => {
        if (m === 'upfront') return s.accrualUpfront(lang)
        if (m === 'monthly') return s.accrualMonthly(lang)
        return s.accrualNone(lang)
    }

    const activePolicies = policies.filter((p) => p.is_active)

    // One flat list — the Eemalolek/Töötamine split was Humaans-legacy
    // grouping; all types are picked together (TASK-307 review feedback).
    const renderTypeGroup = (group: SettingsPolicy[]) => {
        if (group.length === 0) return null
        return (
            <div className="portal-settings__bundle-type-group">
                {group.map((p) => {
                    const checked = bundleForm.policyIds.includes(p.id)
                    return (
                        <button
                            key={p.id}
                            type="button"
                            className={`portal-settings__bundle-type ${checked ? 'is-selected' : ''}`}
                            onClick={() => props.onTogglePolicy(p.id)}
                        >
                            <span className="portal-settings__bundle-type-check">
                                {checked ? <Check size={14} /> : null}
                            </span>
                            <span className="portal-settings__bundle-type-badge">
                                <PortalAbsenceBadge
                                    badgeType={p.badgeType}
                                    label={p.leaveTypeName}
                                />
                            </span>
                            <span className="portal-settings__bundle-type-info">
                                <span className="portal-settings__bundle-type-name">{p.name}</span>
                                <span className="portal-settings__bundle-type-meta">
                                    {p.annual_limit}d · {accrualLabel(p.accrual_method)}
                                </span>
                            </span>
                        </button>
                    )
                })}
            </div>
        )
    }

    return (
        <div>
            <div className="portal-settings__section-header">
                <div>
                    <h3 className="portal-settings__section-title">{b.title(lang)}</h3>
                    <p className="portal-settings__section-description">{b.description(lang)}</p>
                </div>
                <Button
                    variant="primary"
                    size="small"
                    iconLeft={Plus}
                    text={b.createBundle(lang)}
                    onClick={props.onOpenCreate}
                />
            </div>

            {bundles.length === 0 ? (
                <div className="portal-settings__section-empty">{b.noBundles(lang)}</div>
            ) : (
                <div className="portal-settings__data-list">
                    {bundles.map((bundle) => (
                        <div key={bundle.id} className="portal-settings__data-row">
                            <div className="portal-settings__data-info">
                                <div className="portal-settings__data-name">
                                    {bundle.name}{' '}
                                    <span
                                        className={`portal-settings__badge ${bundle.is_active ? 'is-active' : 'is-inactive'}`}
                                    >
                                        {bundle.is_active ? s.active(lang) : s.inactive(lang)}
                                    </span>
                                </div>
                                <div className="portal-settings__data-meta">
                                    {bundle.items.length} {b.typesCountSuffix(lang)} ·{' '}
                                    {bundle.employee_count} {b.employeesCountSuffix(lang)}
                                </div>
                            </div>
                            <div className="portal-settings__data-actions">
                                <button type="button" onClick={() => props.onOpenEdit(bundle)}>
                                    {b.editBundle(lang)}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <PortalConfirmDialog
                open={pendingDeleteId !== null}
                title={t.portal.shared.confirmTitle(lang)}
                message={b.confirmDelete(lang)}
                confirmLabel={t.portal.shared.confirmDelete(lang)}
                cancelLabel={t.portal.shared.confirmCancel(lang)}
                variant="danger"
                onConfirm={() => {
                    if (pendingDeleteId !== null) props.onDelete(pendingDeleteId)
                    setPendingDeleteId(null)
                }}
                onCancel={() => setPendingDeleteId(null)}
            />

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={props.onClose} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {editingBundle ? b.editBundle(lang) : b.createBundle(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={props.onClose}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={b.name(lang)}
                                    value={bundleForm.name}
                                    onChange={(value) => props.onUpdateName(value)}
                                />
                            </div>

                            <div className="portal-slide-over__field">
                                <label>{b.types(lang)}</label>
                                {renderTypeGroup(activePolicies)}
                            </div>

                            <div className="portal-slide-over__field">
                                <label>{b.members(lang)}</label>
                                <PolicyEmployeePicker
                                    lang={lang}
                                    selectedIds={props.employeeIds}
                                    selectedEmployees={props.selectedEmployees}
                                    onAdd={props.onAddEmployee}
                                    onRemove={props.onRemoveEmployee}
                                    onSelectAll={props.onSelectAllEmployees}
                                    onClearAll={props.onClearAllEmployees}
                                />
                            </div>

                            {error ? (
                                <p className="portal-settings__bundle-error">{error}</p>
                            ) : null}
                        </div>
                        <div className="portal-slide-over__footer">
                            {editingBundle ? (
                                <Button
                                    variant="tertiary"
                                    size="small"
                                    text={s.deletePolicy(lang)}
                                    onClick={() => setPendingDeleteId(editingBundle.id)}
                                />
                            ) : null}
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={props.onClose}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={saving ? s.saving(lang) : s.save(lang)}
                                isDisabled={saving || !bundleForm.name}
                                onClick={props.onSave}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
